import React from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import "./RuralGovernance.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

/* ---------------- Inline icon set (no external icon package needed) ---------------- */

const IconHandshake = ({ color = "#1a6b3c" }) => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 22l8-7 8 5 5-3 15 10-4 5-3-2-9 8-11-6-2 3-7-5z"
      stroke={color}
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M22 20l8 6M27 15l9 6" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconTraining = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="22" y="8" width="18" height="13" rx="1.5" stroke="#1a6b3c" strokeWidth="2" />
    <path d="M26 14h10M26 17h6" stroke="#1a6b3c" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="16" r="4.2" stroke="#1a6b3c" strokeWidth="2" />
    <path d="M6 34c0-6 3.5-9.5 9-9.5s9 3.5 9 9.5" stroke="#1a6b3c" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconMap = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 8L6 12v28l11-4 14 4 11-4V8l-11 4-14-4z" stroke="#1a6b3c" strokeWidth="2" strokeLinejoin="round" />
    <path d="M17 8v28M31 12v28" stroke="#1a6b3c" strokeWidth="2" />
    <circle cx="24" cy="22" r="3" stroke="#1a6b3c" strokeWidth="2" />
  </svg>
);

const IconBank = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 18l18-10 18 10" stroke="#1a6b3c" strokeWidth="2" strokeLinejoin="round" />
    <path d="M8 18h32M10 18v18M18 18v18M30 18v18M38 18v18" stroke="#1a6b3c" strokeWidth="2" />
    <path d="M6 42h36" stroke="#1a6b3c" strokeWidth="2" strokeLinecap="round" />
    <path d="M6 36h36" stroke="#1a6b3c" strokeWidth="2" />
  </svg>
);

const IconGroupBadge = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="18" stroke="#1a6b3c" strokeWidth="2" />
    <circle cx="24" cy="19" r="5" stroke="#1a6b3c" strokeWidth="2" />
    <path d="M15 32c1.4-4.5 4.8-6.5 9-6.5s7.6 2 9 6.5" stroke="#1a6b3c" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconHandsHeart = ({ color = "#ef7f3a" }) => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 26c-3-2.8-5.2-5.4-5.2-8.5a4.5 4.5 0 019-1 4.5 4.5 0 019 1c0 3.1-2.2 5.7-5.2 8.5l-3.8 3.6-3.8-3.6z"
      stroke={color}
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M9 30c-2 2.2-3.5 4.8-3.5 8 0 4 3.3 7 7.3 7" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M39 30c2 2.2 3.5 4.8 3.5 8 0 4-3.3 7-7.3 7" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconHeart = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21s-7.5-4.6-10-9.3C.4 8.3 2 4.5 5.6 4c2.1-.3 4 .8 6.4 3 2.4-2.2 4.3-3.3 6.4-3 3.6.5 5.2 4.3 3.6 7.7C19.5 16.4 12 21 12 21z" />
  </svg>
);

const IconUsers = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M2.8 20c.7-3.6 3.3-5.6 6.2-5.6s5.5 2 6.2 5.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M15.5 5.4a3.2 3.2 0 010 6.2M18.8 20c-.5-2.6-1.9-4.4-3.8-5.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconHandHeart = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 38c-4-4-7-8-7-13a7 7 0 0114-1 7 7 0 0114 1c0 5-3 9-7 13l-7 7-7-7z"
      stroke="#ffffff"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    <path
      d="M14 34c-3 3-6 7-6 12 0 5 4 9 9 9h9c4 0 7-2 10-5l9-9c1.5-1.5 1.5-3.7 0-5-1.4-1.3-3.4-1.2-4.8.1l-6.2 6"
      fill="#ef7f3a"
    />
  </svg>
);

/* ------------------------------------------------------------------------------------ */

const RuralGovernance = () => {
  const initiatives = [
    {
      title: "Government Partnership",
      text: "Partnering with the Government of Odisha on a capacity-building mission.",
      icon: <IconHandshake />,
    },
    {
      title: "Training & Development",
      text: "Training newly elected Panchayati Raj Institution (PRI) members.",
      icon: <IconTraining />,
    },
    {
      title: "Wide Reach",
      text: "Covering 20 Gram Panchayats of Kakatpur Block in Puri District.",
      icon: <IconMap />,
    },
    {
      title: "Empowering Leaders",
      text: "Educating over 500 PRI members on the systems of local self-governance.",
      icon: <IconBank />,
    },
  ];

  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="rg-hero">
        <div
          className="rg-hero__bg"
          style={{
            backgroundImage: "url(/assets/images/backgrounds/Rural.png)",
          }}
        ></div>
        <div className="rg-hero__overlay"></div>
        <div className="rg-hero__dots"></div>

        <div className="rg-hero__logo">
          <IconHandHeart />
        </div>

        <svg className="rg-hero__wave" viewBox="0 0 200 260" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2 2c30 20-20 45 10 65s45-10 60 15-10 40 20 60"
            stroke="#ef7f3a"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>

        <Container className="rg-hero__content">
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Rural
            <br />
            <span>Governance</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Vikash supports rural governance by strengthening community
            participation and promoting transparency to enhance local
            decision-making.
          </motion.p>
        </Container>
      </section>

      {/* Introduction Section */}
      <section className="rg-intro">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-center g-5">
              <motion.div className="col-lg-6" variants={staggerItem}>
                <div className="rg-eyebrow">Building Stronger Communities</div>
                <span className="rg-eyebrow__dash"></span>
                <h2 className="rg-heading">
                  Strengthening Local
                  <br />
                  <span>Self-Governance</span>
                </h2>
                <p className="rg-text">
                  In partnership with the Government of Odisha, Vikash
                  has undertaken a capacity-building mission for newly
                  elected Panchayati Raj Institution (PRI) members. We've
                  organized comprehensive training sessions across 20
                  Gram Panchayats in Puri District, reaching over 500 PRI
                  members to discuss the systems and nuances of effective
                  local self-governance.
                </p>
              </motion.div>

              <motion.div className="col-lg-6" variants={staggerItem}>
                <div className="rg-intro__imgwrap">
                  <img
                    src="/assets/images/Subsections/rg1.jpg"
                    alt="Rural governance training session"
                    className="rg-intro__img"
                  />
                  <div className="rg-intro__dots"></div>
                </div>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Capacity Building Programs Section */}
      <section className="rg-programs">
        <div className="rg-programs__leaf">
          <svg viewBox="0 0 120 160" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M60 150V40" stroke="#cddccf" strokeWidth="3" />
            <path d="M60 60c-15-15-35-15-45-5 10 15 30 15 45 5z" fill="#cddccf" />
            <path d="M60 90c15-15 35-15 45-5-10 15-30 15-45 5z" fill="#cddccf" />
            <path d="M60 120c-12-12-28-12-36-4 8 12 24 12 36 4z" fill="#cddccf" />
          </svg>
        </div>

        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="text-center rg-programs__head"
          >
            <div className="rg-programs__icon">
              <IconGroupBadge />
            </div>
            <h2 className="rg-heading rg-heading--center">
              Our Capacity-Building
              <br />
              <span>Programs</span>
            </h2>
            <span className="rg-underline rg-underline--center"></span>
            <p className="rg-subtext">
              We focus on empowering local leaders with the knowledge and
              tools they need to succeed.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4 justify-content-center">
              {initiatives.map((item, index) => (
                <Col md={6} lg={5} key={index}>
                  <motion.div variants={staggerItem} className="h-100">
                    <Card className="rg-card h-100">
                      <Card.Body className="d-flex align-items-start">
                        <div className="rg-card__icon">{item.icon}</div>
                        <div>
                          <Card.Title as="h5">{item.title}</Card.Title>
                          <span className="rg-card__underline"></span>
                          <Card.Text>{item.text}</Card.Text>
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Call To Action Section */}
      <section className="rg-cta">
        <Container>
          <motion.div
            className="rg-cta__panel"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <Row className="align-items-center g-0">
              <Col lg={6} className="rg-cta__left">
                <div className="rg-cta__icon">
                  <IconHandsHeart />
                </div>
                <h2 className="rg-cta__title">
                  Support Community
                  <br />
                  <span>Leadership</span>
                </h2>
                <span className="rg-underline"></span>
                <p className="rg-cta__text">
                  Your contribution helps us train and empower local
                  leaders, fostering stronger and more transparent
                  governance.
                </p>
                <div className="rg-cta__actions">
                  <Button className="rg-btn rg-btn--solid" onClick={openDonation}>
                    <IconHeart /> Donate
                  </Button>
                  <Button
                    className="rg-btn rg-btn--outline"
                    onClick={() => navigate("/interns")}
                  >
                    <IconUsers /> Join Us
                  </Button>
                </div>
              </Col>

              <Col lg={6} className="rg-cta__right">
                <img
                  src="/assets/images/backgrounds/do4.png"
                  alt="Community members in a governance discussion"
                  className="rg-cta__img"
                />
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default RuralGovernance;